import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { Box, Button, FormControlLabel, Paper, Stack, Switch, TextField, Typography } from '@mui/material';

import MarkdownEditor from '~/components/Common/Forms/widgets/MarkdownEditor';

import { Dayjs } from '~/services/TimeService';

import { DynamicSectionType } from '../../context/useDynamicSections';

import ScheduleHeroOffersForm, { parseScheduleHeroOffersFormData } from './ScheduleHeroOffersForm';

interface Props {
  sendDate: Dayjs;
  brand: string;
  countryGroupId: string;
  countryId: string;
  membershipId: string;
  existingOfferId?: string;
  existingSpecialOffer?: boolean;
  existingHeading?: string;
  existingSubheading?: string;
  existingDescriptionHeading?: string;
  existingDescriptionMarkdown?: string;
}

export enum SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES {
  SPECIAL_OFFER = 'customer_offer_section_special_offer',
  HEADING = 'customer_offer_section_heading',
  SUBHEADING = 'customer_offer_section_subheading',
  DESCRIPTION_HEADING = 'customer_offer_section_description_heading',
  DESCRIPTION_MARKDOWN = 'customer_offer_section_description_markdown',
}

export function parseCustomerOfferSectionFormData(formData: FormData, position = -1) {
  const offerIds = parseScheduleHeroOffersFormData(formData).emailHeroOfferIds;
  const getValue = (name: SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES) =>
    (formData.get(name) as string | null | undefined) || '';
  return {
    type: DynamicSectionType.CUSTOM_OFFER,
    offerId: offerIds[0] ?? '',
    specialOffer: getValue(SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES.SPECIAL_OFFER) === 'true',
    heading: getValue(SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES.HEADING),
    subheading: getValue(SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES.SUBHEADING),
    descriptionHeading: getValue(SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES.DESCRIPTION_HEADING),
    descriptionMarkdown: getValue(SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES.DESCRIPTION_MARKDOWN),
    position,
  };
}

const ScheduleCustomerOfferSectionForm = forwardRef<HTMLFormElement, Props>(function ScheduleCustomerOfferSectionForm(
  props,
  ref,
) {
  const {
    sendDate,
    brand,
    countryGroupId,
    countryId,
    membershipId,
    existingOfferId,
    existingSpecialOffer = false,
    existingHeading = '',
    existingSubheading = '',
    existingDescriptionHeading = '',
    existingDescriptionMarkdown = '',
  } = props;

  const [offerIds, setOfferIds] = useState<Array<string>>(existingOfferId ? [existingOfferId] : []);
  const [specialOffer, setSpecialOffer] = useState(existingSpecialOffer);
  const [heading, setHeading] = useState(existingHeading);
  const [subheading, setSubheading] = useState(existingSubheading);
  const [descriptionHeadingValue, setDescriptionHeadingValue] = useState(existingDescriptionHeading);
  const [descriptionValue, setDescriptionValue] = useState(existingDescriptionMarkdown);
  const [showHeader, setShowHeader] = useState(!!(existingHeading || existingSubheading));
  const [showDescription, setShowDescription] = useState(!!(existingDescriptionHeading || existingDescriptionMarkdown));

  const headerFormRef = useRef<HTMLFormElement>(null);
  const descriptionFormRef = useRef<HTMLFormElement>(null);
  const offersFormRef = useRef<HTMLFormElement>(null);
  const editorIdPrefix = useRef(`customer-offer-${Math.random().toString(36).slice(2)}`);

  useImperativeHandle(ref, () => {
    const baseForm = headerFormRef.current || descriptionFormRef.current;
    return {
      ...(baseForm || ({} as HTMLFormElement)),
      reportValidity: () => {
        const headerFormValid = headerFormRef.current?.reportValidity() ?? true;
        const descriptionFormValid = descriptionFormRef.current?.reportValidity() ?? true;
        const offersFormValid = offersFormRef.current?.reportValidity() ?? true;
        return headerFormValid && descriptionFormValid && offersFormValid;
      },
      makeFormData: () => {
        const combinedFormData = new FormData();
        for (const form of [headerFormRef, descriptionFormRef, offersFormRef]) {
          if (form.current) {
            for (const [key, value] of new FormData(form.current)) {
              combinedFormData.append(key, value);
            }
          }
        }
        return combinedFormData;
      },
    };
  });

  const handleToggleHeader = () => {
    if (showHeader) {
      setHeading('');
      setSubheading('');
    }
    setShowHeader(!showHeader);
  };

  const handleToggleDescription = () => {
    if (showDescription) {
      setDescriptionHeadingValue('');
      setDescriptionValue('');
    }
    setShowDescription(!showDescription);
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        border: specialOffer ? 2 : 0,
        borderColor: specialOffer ? 'success.main' : 'divider',
        bgcolor: specialOffer ? 'success' : 'background.paper',
      }}
    >
      <Stack spacing={3}>
        <form ref={headerFormRef}>
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={<Switch checked={specialOffer} onChange={(e) => setSpecialOffer(e.target.checked)} />}
                label={
                  <Typography variant="body1" fontWeight={specialOffer ? 600 : 400}>
                    Special Offer
                  </Typography>
                }
              />
              <Button
                variant={showHeader ? 'outlined' : 'contained'}
                color={showHeader ? 'error' : 'primary'}
                size="small"
                onClick={handleToggleHeader}
                sx={{ minWidth: 140 }}
              >
                {showHeader ? 'Remove Header' : 'Add Header'}
              </Button>
              <Button
                variant={showDescription ? 'outlined' : 'contained'}
                color={showDescription ? 'error' : 'primary'}
                size="small"
                onClick={handleToggleDescription}
                sx={{ minWidth: 180 }}
              >
                {showDescription ? 'Remove Description' : 'Add Description'}
              </Button>
            </Box>

            {showHeader && (
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Header"
                  value={heading}
                  onChange={(e) => setHeading(e.target.value)}
                  variant="outlined"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Subheader"
                  value={subheading}
                  onChange={(e) => setSubheading(e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Stack>
            )}

            <input
              type="hidden"
              name={SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES.SPECIAL_OFFER}
              value={String(specialOffer)}
            />
            <input type="hidden" name={SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES.HEADING} value={heading} />
            <input type="hidden" name={SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES.SUBHEADING} value={subheading} />
          </Stack>
        </form>

        <ScheduleHeroOffersForm
          ref={offersFormRef}
          sendDate={sendDate}
          brand={brand}
          countryGroupId={countryGroupId}
          countryId={countryId}
          membershipId={membershipId}
          offerIds={offerIds}
          onOfferIdsChange={setOfferIds}
          maxOfferLimit={1}
        />

        <form ref={descriptionFormRef}>
          <Stack spacing={2}>
            {showDescription && (
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Description Heading"
                  value={descriptionHeadingValue}
                  onChange={(e) => setDescriptionHeadingValue(e.target.value)}
                  variant="outlined"
                  size="small"
                />
                <MarkdownEditor
                  label="Description"
                  value={descriptionValue}
                  onChange={setDescriptionValue}
                  wrapperId={`${editorIdPrefix.current}-description`}
                />
              </Stack>
            )}

            <input
              type="hidden"
              name={SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES.DESCRIPTION_HEADING}
              value={descriptionHeadingValue}
            />
            <input
              type="hidden"
              name={SCHEDULE_CUSTOM_OFFER_SECTION_FORM_NAMES.DESCRIPTION_MARKDOWN}
              value={descriptionValue}
            />
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
});

ScheduleCustomerOfferSectionForm.displayName = 'ScheduleCustomerOfferSectionForm';
export default ScheduleCustomerOfferSectionForm;
